import ProtectedRouter from "@/context/auth/ProtectedRouter";
import { TenantProvider } from "@/context/TenantContext";
import { useTenant } from "@/context/useTenant";
import MainLayout from "@/layouts/MainLayout";
import StarterLayout from "@/layouts/StarterLayout";
import NotFoundPage from "@/pages/NotFoundPage";
import routes from "@/utils/routes";
import propertyPortalRoutes from "@/project/propertyPortal/utils/routes";
import PropertyPortalApp from "@/project/propertyPortal/app"; // 👈 Import your Property Portal App
import SchemasListPage from "@/pages/SchemaManagement/SchemasListPage";
import { BrowserRouter, Outlet, Route, Routes } from "react-router-dom";

function App() {
  return (
    <TenantProvider>
      <AppWithRouter />
    </TenantProvider>
  );
}

const AppWithRouter = () => {
  const { tenantId } = useTenant();

  // If no tenant ID, show a simple fallback
  if (!tenantId) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <h1 className="mb-4 text-2xl font-bold">Tenant ID Required</h1>
          <p className="text-gray-600">
            Please provide a tenant ID in the URL path (e.g.,
            /your-tenant-id/login)
          </p>
        </div>
      </div>
    );
  }

  // Merge main and property portal routes
  const mainPublicRoutes = routes.find(m => m.type === "public")?.routes ?? [];
  const mainAuthRoutes = routes.find(m => m.type === "auth")?.routes ?? [];
  const propertyPortalAuthRoutes =
    propertyPortalRoutes.find(m => m.type === "auth")?.routes ?? [];
  const propertyPortalPublicRoutes =
    propertyPortalRoutes.find(m => m.type === "public")?.routes ?? [];

  // Combine
  const publicRoutes = [...propertyPortalPublicRoutes, ...mainPublicRoutes];
  const authRoutes = [...propertyPortalAuthRoutes, ...mainAuthRoutes];

  return (
    <BrowserRouter basename={`/${tenantId}`}>
      <Routes>
        <Route element={<TopLevelComponent />}>
          {/* ================= PUBLIC ROUTES ================= */}
          {publicRoutes.map(({ path, element: Page }) => (
            <Route key={path} path={path} element={<StarterLayout />}>
              <Route index element={<Page />} />
            </Route>
          ))}

          {/* ================= PROPERTY PORTAL APP ================= */}
          <Route path="property-portal/*" element={<PropertyPortalApp />} />

          {/* ================= PROTECTED ROUTES (includes root path) ================= */}
          <Route element={<ProtectedRouter />}>
            <Route element={<MainLayout />}>
              {/* Root path - Show Architecture One Schemas Page */}
              <Route index element={<SchemasListPage />} />
              {authRoutes.map(({ path, element: Page }) => (
                <Route key={path} path={path} element={<Page />} />
              ))}
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Route>
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

// Keeps nesting consistent
const TopLevelComponent = () => {
  return <Outlet />;
};

export default App;
